import { useState, useEffect } from 'react';

interface MobileDetectionOptions {
    breakpoint?: number;
    includeTablet?: boolean;
}

const useIsMobile = (options: MobileDetectionOptions = {}) => {
    const { breakpoint = 768, includeTablet = false } = options;

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIsMobile = () => {
            // Method 1: Screen width detection
            const screenWidth = window.innerWidth;
            const isMobileByWidth = screenWidth <= breakpoint;

            // Method 2: User agent detection
            const userAgent = navigator.userAgent.toLowerCase();
            const isMobileByUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

            // Method 3: Touch capability detection
            const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

            // Method 4: CSS media query detection
            const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
            const isMobileByMediaQuery = mediaQuery.matches;

            // Combine detection methods
            let mobileDetected = isMobileByWidth || isMobileByUserAgent || isMobileByMediaQuery;

            // If includeTablet is false, exclude iPad from mobile detection
            if (!includeTablet && /ipad/i.test(userAgent)) {
                mobileDetected = false;
            }

            // Additional check for desktop browsers with touch screens
            if (hasTouchScreen && !isMobileByUserAgent && screenWidth > 1024) {
                mobileDetected = false;
            }

            setIsMobile(mobileDetected);
        };

        // Initial check
        checkIsMobile();

        // Listen for window resize events
        const handleResize = () => {
            checkIsMobile();
        };

        window.addEventListener('resize', handleResize);

        // Listen for orientation change on mobile devices
        window.addEventListener('orientationchange', () => {
            // Small delay to ensure the orientation change is complete
            setTimeout(checkIsMobile, 100);
        });

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [breakpoint, includeTablet]);

    return isMobile;
};

export default useIsMobile;
